/**
 * InboundPruefprotokoll — read-only "Formale Prüfung (EN 16931)" panel (IDV-4,
 * architecture-inbound-datev.md §5.7). Renders `doc.validation.findings`
 * (already persisted, max 50 — never truncated further here) with the
 * whitelist-approved, NEUTRAL wording defined in §5.7. This panel is purely
 * informational — it NEVER implies a document was rejected, is invalid, or is
 * "geprüft"/"rechtssicher" in a compliance sense (Senatex/Barlis: formal norm
 * deviations do not affect the legal validity of the invoice or the input VAT
 * deduction).
 *
 * A Kleinbetragsrechnung (§33 UStDV, gross total ≤ 250 €) gets a PROACTIVE note
 * — reduced mandatory fields are expected and normal for such invoices, so the
 * finding count should not read as a defect (e-rechnung 🟢 "nicht erschrecken").
 *
 * Whitelist (§5.7, `posteingang.json` `_whitelist`): none of the i18n keys this
 * component reads may claim a document is "ungültig", "unvollständig",
 * "mangelhaft" (as a positive assertion), "geprüft" (past-tense verdict),
 * "rechtssicher", "gültig", "GoBD-konform" or "GoBD-zertifiziert". The one
 * negated exception is the mandatory disclaimer text, which is dictated
 * verbatim by §5.7 and explicitly NEGATES "mangelhaft" ("… ohne mangelhaft zu
 * sein") to reassure, not assert — see changelog.md for this known,
 * architecture-mandated exception.
 *
 * @module InboundPruefprotokoll
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InboundValidationFinding } from '../../types/inboundTypes';

/** Kleinbetragsrechnung threshold (§33 UStDV), gross total in EUR. */
const KLEINBETRAG_THRESHOLD_EUR = 250;

interface InboundPruefprotokollProps {
  /** `doc.validation.findings` — read-only, rendered as Code + Message + Severity. */
  findings: InboundValidationFinding[];
  /** `doc.extracted?.grossTotal` — drives the proactive §33 UStDV note. */
  grossTotal: number | null | undefined;
}

/**
 * Renders the neutral EN-16931 formal-check protocol for one inbound document.
 *
 * @param props see {@link InboundPruefprotokollProps}.
 * @returns the panel markup.
 */
const InboundPruefprotokoll: React.FC<InboundPruefprotokollProps> = ({
  findings,
  grossTotal,
}) => {
  const { t } = useTranslation('posteingang');

  const safeFindings = Array.isArray(findings) ? findings : [];
  const hasFindings = safeFindings.length > 0;
  const isKleinbetrag =
    typeof grossTotal === 'number' &&
    Number.isFinite(grossTotal) &&
    grossTotal > 0 &&
    grossTotal <= KLEINBETRAG_THRESHOLD_EUR;

  return (
    <div
      className="border border-hairline rounded-xl p-4 mb-5"
      data-testid="inbound-pruefprotokoll"
    >
      <h3 className="text-sm font-semibold text-secondary mb-2">
        {t('pruefprotokoll.title', 'Formale Prüfung (EN 16931)')}
      </h3>

      {isKleinbetrag && (
        <p className="text-xs text-accent-ink mb-2">
          {t(
            'pruefprotokoll.kleinbetragHint',
            'Kleinbetragsrechnung (§33 UStDV) — reduzierte Pflichtangaben',
          )}
        </p>
      )}

      {hasFindings ? (
        <>
          <p className="text-sm text-secondary mb-3">
            {t(
              'pruefprotokoll.findingsHint',
              'Hinweis: Das Format weicht in {{count}} Punkten von der Norm EN 16931 ab.',
              { count: safeFindings.length },
            )}
          </p>
          <ul className="space-y-2 mb-3">
            {safeFindings.map((f, i) => (
              <li
                key={`${f.code ?? 'finding'}-${i}`}
                className="text-sm text-secondary border-t border-hairline pt-2"
                data-finding-severity={f.severity}
              >
                <span className="text-faint mr-2">
                  {f.severity === 'error'
                    ? t('pruefprotokoll.severity.error', 'Fehler')
                    : t('pruefprotokoll.severity.warning', 'Hinweis')}
                </span>
                <span className="font-mono text-xs text-muted mr-2">
                  {f.code}
                </span>
                <span>{f.message}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-sm text-secondary mb-3">
          {t(
            'pruefprotokoll.noFindings',
            'Keine formalen Normabweichungen festgestellt.',
          )}
        </p>
      )}

      <p className="text-xs text-faint">
        {t(
          'pruefprotokoll.disclaimer',
          'Formale Abweichungen von der Norm EN 16931 bedeuten nicht schon für sich, dass die Rechnung steuerlich unwirksam ist oder der Vorsteuerabzug entfällt. Kleinbetragsrechnungen (§ 33 UStDV, bis 250 € brutto) und Fahrausweise (§ 34 UStDV) haben von Gesetzes wegen reduzierte Pflichtangaben und lösen Norm-Hinweise aus, ohne mangelhaft zu sein. Die steuerliche Beurteilung und die Entscheidung über den Vorsteuerabzug obliegen Ihnen bzw. Ihrer Steuerberatung.',
        )}
      </p>
    </div>
  );
};

export default InboundPruefprotokoll;
